import { useEffect, useState } from 'react';
import Modal from 'modules/common/components/Modal';
import { DISCOUNT_VALUE_TYPES, PRODUCT_TYPE } from '../../constants';
import type { DiscountProduct } from '../../types/discount.types';
import discountsStrings from '../../strings';
import { useCreateBundleForm } from './useCreateBundleForm';
import SelectProductsModal from '../SelectProductsModal/SelectProductsModal';
import {
  BundleDiscountControls,
  BundleInfoLabel,
  BundleInfoPanel,
  BundleInfoText,
  BundlePriceDiscountRow,
  ClearBundleButton,
  DateRangeRow,
  InlineError,
  ModalBodyContent,
  ModalField,
  ModalInput,
  ModalLabel,
  ModalSelect,
  ReadOnlyModalInput,
  SelectedBundleCheck,
  SelectedBundleHeader,
  SelectBundleInput,
  SelectBundleLabel,
} from '../../views/Discounts.style';

interface CreateBundleModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
}

const today = new Date().toISOString().slice(0, 10);

const CreateBundleModal = ({ isOpen, isSubmitting, onClose }: CreateBundleModalProps) => {
  const [selectedBundle, setSelectedBundle] = useState<DiscountProduct | null>(null);
  const [isSelectBundleOpen, setIsSelectBundleOpen] = useState(false);
  const [draftBundle, setDraftBundle] = useState<DiscountProduct | null>(null);

  const { register, errors, watchedStartDate, watchedDiscountType, handleFormSubmit } =
    useCreateBundleForm({ isOpen, selectedBundle });

  useEffect(() => {
    if (isOpen) return;
    setSelectedBundle(null);
    setIsSelectBundleOpen(false);
    setDraftBundle(null);
  }, [isOpen]);

  const handleOpenSelectBundle = () => {
    setDraftBundle(selectedBundle);
    setIsSelectBundleOpen(true);
  };

  const handleApplyBundle = () => {
    if (!draftBundle) return;
    setSelectedBundle(draftBundle);
    setDraftBundle(null);
    setIsSelectBundleOpen(false);
  };

  const handleCloseSelectBundle = () => {
    setDraftBundle(null);
    setIsSelectBundleOpen(false);
  };

  return (
    <>
      <Modal
        toggle={isOpen}
        title={discountsStrings.createBundleTitle}
        onClose={onClose}
        onCancelHandler={onClose}
        cancelActionLabel={discountsStrings.cancelBtn}
        onConfirmHandler={handleFormSubmit}
        confirmActionLabel={discountsStrings.createBundleSubmitBtn}
        confirmActionLoading={isSubmitting}
        disableConfirmButton={!selectedBundle}
        closeOnClickOutside={false}
        size="lg"
      >
        <ModalBodyContent $fixedHeight>
          <SelectBundleLabel>{discountsStrings.selectBundleLabel}</SelectBundleLabel>
          {selectedBundle ? (
            <SelectedBundleHeader>
              <SelectedBundleCheck>✓</SelectedBundleCheck>
              <span>{selectedBundle.productName}</span>
              <ClearBundleButton type="button" onClick={handleOpenSelectBundle}>
                {discountsStrings.changeBundleBtn}
              </ClearBundleButton>
            </SelectedBundleHeader>
          ) : (
            <SelectBundleInput
              type="text"
              readOnly
              placeholder={discountsStrings.searchBundlePlaceholder}
              onClick={handleOpenSelectBundle}
            />
          )}

          <DateRangeRow>
            <ModalField>
              <ModalLabel>{discountsStrings.fromLabel}</ModalLabel>
              <ModalInput type="date" min={today} {...register('startDate')} />
              {errors.startDate && <InlineError>{errors.startDate.message}</InlineError>}
            </ModalField>
            <ModalField>
              <ModalLabel>{discountsStrings.toLabel}</ModalLabel>
              <ModalInput type="date" min={watchedStartDate || today} {...register('endDate')} />
              {errors.endDate && <InlineError>{errors.endDate.message}</InlineError>}
            </ModalField>
          </DateRangeRow>

          <BundlePriceDiscountRow>
            <ModalField>
              <ModalLabel>{discountsStrings.originalPriceFieldLabel}</ModalLabel>
              <ReadOnlyModalInput
                value={`${(selectedBundle?.originalPrice ?? 0).toFixed(2)} ${discountsStrings.currency}`}
                readOnly
                disabled
              />
            </ModalField>
            <ModalField>
              <ModalLabel>{discountsStrings.discountTypeLabel}</ModalLabel>
              <BundleDiscountControls>
                <ModalInput
                  type="number"
                  min="0"
                  step="any"
                  placeholder={
                    watchedDiscountType === DISCOUNT_VALUE_TYPES.Percent
                      ? discountsStrings.discountPercentagePlaceholder
                      : discountsStrings.discountAmountPlaceholder
                  }
                  {...register('discountValue')}
                />
                <ModalSelect {...register('discountType')}>
                  <option value={DISCOUNT_VALUE_TYPES.Percent}>%</option>
                  <option value={DISCOUNT_VALUE_TYPES.Amount}>{discountsStrings.currency}</option>
                </ModalSelect>
              </BundleDiscountControls>
              {errors.discountValue && <InlineError>{errors.discountValue.message}</InlineError>}
            </ModalField>
          </BundlePriceDiscountRow>

          {selectedBundle?.description ? (
            <BundleInfoPanel>
              <BundleInfoLabel>{discountsStrings.bundleDescriptionLabel}</BundleInfoLabel>
              <BundleInfoText>{selectedBundle.description}</BundleInfoText>
            </BundleInfoPanel>
          ) : null}
        </ModalBodyContent>
      </Modal>

      <SelectProductsModal
        isOpen={isSelectBundleOpen}
        stacked
        selectedProducts={draftBundle ? [draftBundle] : []}
        onToggleProduct={setDraftBundle}
        onClose={handleCloseSelectBundle}
        onSave={handleApplyBundle}
        productType={PRODUCT_TYPE.Bundle}
        offerEligibleOnly
        title={discountsStrings.selectBundleTitle}
        searchPlaceholder={discountsStrings.searchBundlePlaceholder}
      />
    </>
  );
};

export default CreateBundleModal;
